import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@radix-ui/react-tooltip";
import ColumnVisibilityToggle from "./column-visibility-toggle";
import { TableColumn } from "./data-table";
import { cn } from "@/lib/utils";
import { useColumnWidths } from "@/hooks/useColumnWidths";

interface SkeletonTableProps {
  columsNumber: number;
  rowsNumber: number;
  title?: string;
  description?: string;
  columns?: TableColumn[];
  enableRowSelection?: boolean;
  tableId?: string;
}

export default function SkeletonTable({
  columsNumber,
  rowsNumber,
  title,
  description,
  columns,
  enableRowSelection = false,
  tableId,
}: SkeletonTableProps) {
  const { getWidth } = useColumnWidths(tableId);
  return (
    <div>
      {/* Table Skeleton */}
      <Card
        className={cn("transition-opacity duration-20 w-full hidden sm:block")}
      >
        <CardHeader className="gap-0 pb-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1 flex-1 min-w-0 mr-4 gap-1 flex items-center">
              <div>
                {title && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardTitle className="sm:text-lg truncate cursor-default">
                          {title}
                        </CardTitle>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-md"
                      >
                        <p>{title}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {description && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardDescription className="text-sm truncate cursor-default">
                          {description}
                        </CardDescription>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-md"
                      >
                        <p>{description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-0">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <Table>
                <TableHeader className="rounded-t-lg">
                  <TableRow>
                    {enableRowSelection && (
                      <TableHead className="w-10">
                        <Skeleton className="h-4 w-4 rounded" />
                      </TableHead>
                    )}
                    {columns
                      ? columns.map((column, index) => {
                          const w = getWidth(column.key);
                          const style: React.CSSProperties | undefined =
                            typeof w === "number"
                              ? { width: `${w}px`, minWidth: `${w}px` }
                              : undefined;
                          return (
                            <TableHead
                              key={column.key}
                              data-column-key={column.key}
                              className={`text-muted-foreground text-sm font-normal ${column.className} ${
                                column.sortable
                                  ? "cursor-pointer hover:text-foreground transition-colors"
                                  : ""
                              }`}
                              style={style}
                            >
                              <div className="flex items-center gap-2 select-none">
                                <span>{column.label}</span>
                              </div>
                            </TableHead>
                          );
                        })
                      : Array.from({ length: columsNumber }).map((_, index) => (
                          <TableHead key={index}>
                            <Skeleton className="h-4 w-20" />
                          </TableHead>
                        ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: rowsNumber }).map((_, index) => (
                    <RowSkeleton
                      key={index}
                      columsNumber={columsNumber}
                      enableRowSelection={enableRowSelection}
                      columns={columns}
                      getWidth={getWidth}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination skeleton */}
          <div className="mt-2 px-2 py-1">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
              {/* Izquierda: info de elementos */}
              <div className="hidden md:flex">
                <Skeleton className="h-4 w-36" />
              </div>
              {/* Centro: botones de página */}
              <div className="flex items-center justify-center gap-1">
                <Skeleton className="h-7 w-7 rounded-md" />
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-7 rounded-md" />
                  ))}
                </div>
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
              {/* Derecha: ir a página */}
              <div className="hidden md:flex items-center justify-end gap-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-7 w-16 rounded-md" />
                <Skeleton className="h-4 w-8" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const RowSkeleton = ({
  columsNumber,
  enableRowSelection = false,
  columns,
  getWidth,
}: {
  columsNumber: number;
  enableRowSelection?: boolean;
  columns?: TableColumn[];
  getWidth?: (key: string) => number | undefined;
}) => (
  <TableRow className="h-9">
    {enableRowSelection && (
      <TableCell className="w-10 py-0">
        <Skeleton className="h-4 w-4 rounded" />
      </TableCell>
    )}
    {columns && columns.length > 0
      ? columns.map((column) => {
          const w = getWidth?.(column.key);
          const style: React.CSSProperties | undefined =
            typeof w === "number"
              ? { width: `${w}px`, minWidth: `${w}px` }
              : undefined;
          return (
            <TableCell
              key={column.key}
              data-column-key={column.key}
              className={`py-0 ${column.className ?? ""}`}
              style={style}
            >
              <Skeleton className="h-4 w-20" />
            </TableCell>
          );
        })
      : Array.from({ length: columsNumber }).map((_, index) => (
          <TableCell key={index} className="py-0">
            <Skeleton className="h-4 w-20" />
          </TableCell>
        ))}
    <TableCell className="py-0 text-right">
      <Skeleton className="h-4 w-6 rounded ml-auto" />
    </TableCell>
  </TableRow>
);
